import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CONTRACTOR_ENTRY_RULES } from '@propmgt/shared';
import type { ContractorJobDetailResponse, ScheduleProposalDTO, WorkOrderCostDTO } from '@propmgt/shared';
import { api, ApiError, errorMessage } from '../../api/client';
import { formatDateTime } from '../../lib/date';
import { formatCents } from '../../lib/money';
import { blobToBase64, isImageFile, prepareMoveOutUpload } from '../../lib/upload';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { CONTRACTOR_JOBS_KEY, locationLabel } from './Jobs';

const jobKey = (id: string) => ['contractor', 'job', id] as const;

/** A datetime-local value ("2026-10-01T09:00", browser-local) → a strict UTC ISO string. */
function localToIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function dollarsToCents(v: string): number | null {
  const n = Number(v.replace(/[$,\s]/g, ''));
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}

/** Open a job document (photo / own invoice) via a short-lived signed URL, in a new tab. */
async function openDocument(jobId: string, documentId: string): Promise<void> {
  const { url } = await api.contractor.documentUrl(jobId, documentId);
  window.open(url, '_blank', 'noopener');
}

function useRefresh(id: string) {
  const qc = useQueryClient();
  return (data?: ContractorJobDetailResponse) => {
    if (data) qc.setQueryData(jobKey(id), data);
    void qc.invalidateQueries({ queryKey: jobKey(id) });
    void qc.invalidateQueries({ queryKey: CONTRACTOR_JOBS_KEY });
  };
}

/**
 * Friendly copy for the proposal/scheduling error codes (never show a raw `snake_case` code to the
 * contractor). Falls back to the generic message.
 */
export function friendlyScheduleError(err: unknown): string {
  const code = err instanceof ApiError ? err.body?.error : undefined;
  switch (code) {
    case 'proposal_limit_reached':
    case 'reschedule_limit_reached':
      return `You've already proposed ${CONTRACTOR_ENTRY_RULES.maxProposalsPer24h} times for this job in the last 24 hours. Please wait, or call the office to arrange a time.`;
    case 'insufficient_notice':
      return 'The window must start at least 24 hours from now — the tenant is legally entitled to 24 hours’ notice.';
    case 'entry_outside_business_hours':
      return 'Pick a window on a weekday (Monday–Friday) between 8 AM and 6 PM Pacific, on a single day.';
    case 'entry_window_too_long':
      return `The window can be at most ${CONTRACTOR_ENTRY_RULES.maxWindowHours} hours long.`;
    case 'entry_end_before_start':
      return 'The end time must be after the start time.';
    case 'work_order_terminal':
      return 'This job is closed.';
    case 'invalid_transition':
      return 'This job can’t be scheduled right now.';
    default:
      return errorMessage(err);
  }
}

/** Propose an entry window — the OWNER confirms it; only then is the tenant sent a notice. */
function ProposeForm({ id, hasOpen }: { id: string; hasOpen: boolean }) {
  const refresh = useRefresh(id);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [note, setNote] = useState('');
  const [ok, setOk] = useState(false);
  const m = useMutation({
    mutationFn: () => {
      const s = localToIso(start);
      const e = localToIso(end);
      if (!s || !e) throw new Error('Pick a start and end time.');
      return api.contractor.proposeSchedule(id, { windowStart: s, windowEnd: e, note: note.trim() || null });
    },
    onSuccess: () => {
      setNote('');
      setOk(true);
      refresh();
    },
  });
  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        setOk(false);
        m.mutate();
      }}
    >
      <h3>Propose a time</h3>
      <p className="field-hint">
        Suggest when you can do the work. The owner confirms it and then sends the tenant the legal 24-hour
        entry notice — nothing goes to the tenant until then. Pick a window at least 24 hours from now,
        Monday–Friday between 8 AM and 6 PM (Pacific), up to {CONTRACTOR_ENTRY_RULES.maxWindowHours} hours long.
        {hasOpen ? ' Sending a new time replaces your pending one.' : ''}
      </p>
      {ok && <StatusMessage tone="ok">Sent to the owner for confirmation.</StatusMessage>}
      {m.isError && <StatusMessage tone="err">{friendlyScheduleError(m.error)}</StatusMessage>}
      <div>
        <label htmlFor="prop-start">Start</label>
        <input id="prop-start" type="datetime-local" required value={start} onChange={(e) => setStart(e.target.value)} />
      </div>
      <div>
        <label htmlFor="prop-end">End</label>
        <input id="prop-end" type="datetime-local" required value={end} onChange={(e) => setEnd(e.target.value)} />
      </div>
      <div>
        <label htmlFor="prop-note">Note for the owner (optional — not shown to the tenant)</label>
        <input id="prop-note" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <button type="submit" disabled={m.isPending}>
        {m.isPending ? 'Sending…' : 'Propose time'}
      </button>
    </form>
  );
}

function ProposalRow({ jobId, p }: { jobId: string; p: ScheduleProposalDTO }) {
  const refresh = useRefresh(jobId);
  const withdraw = useMutation({ mutationFn: () => api.contractor.withdrawProposal(p.id), onSuccess: () => refresh() });
  const label =
    p.status === 'proposed' ? 'waiting for the owner' : p.status === 'superseded' ? 'replaced' : p.status;
  return (
    <li className="card stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span>
          {formatDateTime(p.windowStart)} – {formatDateTime(p.windowEnd)}
        </span>
        <span className="badge">{label}</span>
      </div>
      {p.reviewNote && <p className="muted" style={{ margin: 0 }}>Owner note: {p.reviewNote}</p>}
      {p.status === 'proposed' && (
        <div>
          <button type="button" className="secondary" disabled={withdraw.isPending} onClick={() => withdraw.mutate()}>
            {withdraw.isPending ? 'Withdrawing…' : 'Withdraw'}
            <span className="visually-hidden"> proposed time {formatDateTime(p.windowStart)}</span>
          </button>
        </div>
      )}
      {withdraw.isError && <StatusMessage tone="err">{friendlyScheduleError(withdraw.error)}</StatusMessage>}
    </li>
  );
}

function ResolveForm({ id }: { id: string }) {
  const refresh = useRefresh(id);
  const [resolutionNote, setResolutionNote] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api.contractor.setStatus(id, { status: 'resolved', resolutionNote: resolutionNote.trim() || null }),
    onSuccess: refresh,
  });
  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <h3>Mark the work done</h3>
      {m.isError && <StatusMessage tone="err">{errorMessage(m.error)}</StatusMessage>}
      <div>
        <label htmlFor="resolve-note">What was done (for the owner)</label>
        <textarea
          id="resolve-note"
          rows={3}
          maxLength={4000}
          value={resolutionNote}
          onChange={(e) => setResolutionNote(e.target.value)}
        />
      </div>
      <button type="submit" disabled={m.isPending}>
        {m.isPending ? 'Saving…' : 'Mark resolved'}
      </button>
    </form>
  );
}

function NoteForm({ id }: { id: string }) {
  const refresh = useRefresh(id);
  const [note, setNote] = useState('');
  const m = useMutation({
    mutationFn: () => api.contractor.note(id, { note: note.trim() }),
    onSuccess: (d) => {
      setNote('');
      refresh(d);
    },
  });
  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (note.trim()) m.mutate();
      }}
    >
      <h3>Add a note</h3>
      <p className="field-hint">Notes go to the owner only — the tenant does not see them.</p>
      {m.isError && <StatusMessage tone="err">{errorMessage(m.error)}</StatusMessage>}
      <label htmlFor="job-note" className="visually-hidden">
        Note
      </label>
      <textarea id="job-note" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
      <button type="submit" disabled={m.isPending || !note.trim()}>
        {m.isPending ? 'Saving…' : 'Save note'}
      </button>
    </form>
  );
}

function PhotoUpload({ id }: { id: string }) {
  const refresh = useRefresh(id);
  const [files, setFiles] = useState<File[]>([]);
  const m = useMutation({
    mutationFn: async () => {
      // Compress client-side (≤2048 px JPEG rendition, HEIC → JPEG) — the move-out helper. Only the
      // rendition is uploaded for job photos (progress photos, sent over a phone connection).
      const photos = [];
      for (const f of files) {
        const prepared = await prepareMoveOutUpload(f);
        photos.push({
          filename: prepared.primary.filename,
          contentType: prepared.primary.contentType,
          contentBase64: await blobToBase64(prepared.primary.blob),
          capturedAt: prepared.capturedAt,
        });
      }
      return api.contractor.addPhotos(id, { photos });
    },
    onSuccess: (d) => {
      setFiles([]);
      refresh(d);
    },
  });
  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (files.length) m.mutate();
      }}
    >
      <h3>Add photos</h3>
      {m.isError && <StatusMessage tone="err">{errorMessage(m.error)}</StatusMessage>}
      <label htmlFor="job-photos">Photos (up to 10)</label>
      <input
        id="job-photos"
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => setFiles(Array.from(e.target.files ?? []).filter(isImageFile).slice(0, 10))}
      />
      <button type="submit" disabled={m.isPending || files.length === 0}>
        {m.isPending ? 'Uploading…' : `Upload ${files.length || ''} photo${files.length === 1 ? '' : 's'}`}
      </button>
    </form>
  );
}

function CostForm({ id }: { id: string }) {
  const refresh = useRefresh(id);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [invoice, setInvoice] = useState<File | null>(null);
  const [ok, setOk] = useState(false);
  const m = useMutation({
    mutationFn: async () => {
      const cents = dollarsToCents(amount);
      if (cents == null) throw new Error('Enter an amount greater than $0.');
      let inv = null;
      if (invoice) {
        const prepared = await prepareMoveOutUpload(invoice); // PDFs pass through untouched
        inv = {
          filename: prepared.primary.filename,
          contentType: prepared.primary.contentType,
          contentBase64: await blobToBase64(prepared.primary.blob),
        };
      }
      return api.contractor.submitCost(id, { amountCents: cents, description: description.trim(), invoice: inv });
    },
    onSuccess: () => {
      setAmount('');
      setDescription('');
      setInvoice(null);
      setOk(true);
      refresh();
    },
  });
  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        setOk(false);
        m.mutate();
      }}
    >
      <h3>Submit a cost</h3>
      <p className="field-hint">Costs are not final until the owner approves them.</p>
      {ok && <StatusMessage tone="ok">Cost submitted for approval.</StatusMessage>}
      {m.isError && <StatusMessage tone="err">{errorMessage(m.error)}</StatusMessage>}
      <div>
        <label htmlFor="cost-amount">Amount (USD)</label>
        <input
          id="cost-amount"
          inputMode="decimal"
          required
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="250.00"
        />
      </div>
      <div>
        <label htmlFor="cost-desc">Description</label>
        <textarea
          id="cost-desc"
          rows={2}
          required
          maxLength={1000}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Parts + labor"
        />
      </div>
      <div>
        <label htmlFor="cost-invoice">Invoice or receipt (photo or PDF, optional)</label>
        <input
          id="cost-invoice"
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => setInvoice(e.target.files?.[0] ?? null)}
        />
      </div>
      <button type="submit" disabled={m.isPending}>
        {m.isPending ? 'Submitting…' : 'Submit cost'}
      </button>
    </form>
  );
}

function CostRow({ jobId, cost, locked }: { jobId: string; cost: WorkOrderCostDTO; locked: boolean }) {
  const refresh = useRefresh(jobId);
  const withdraw = useMutation({ mutationFn: () => api.contractor.withdrawCost(cost.id), onSuccess: () => refresh() });
  return (
    <li className="card stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>{formatCents(cost.amountCents)}</strong>
        <span className="badge">{cost.status === 'submitted' ? 'pending approval' : cost.status}</span>
      </div>
      <p style={{ margin: 0 }}>{cost.description}</p>
      {cost.reviewNote && <p className="muted" style={{ margin: 0 }}>Owner note: {cost.reviewNote}</p>}
      <div className="row">
        {cost.invoiceDocumentId && (
          <button type="button" className="secondary" onClick={() => void openDocument(jobId, cost.invoiceDocumentId!)}>
            View invoice
          </button>
        )}
        {cost.status === 'submitted' && !locked && (
          <button type="button" className="secondary" disabled={withdraw.isPending} onClick={() => withdraw.mutate()}>
            {withdraw.isPending ? 'Withdrawing…' : 'Withdraw'}
            <span className="visually-hidden"> cost of {formatCents(cost.amountCents)}</span>
          </button>
        )}
      </div>
      {withdraw.isError && <StatusMessage tone="err">{errorMessage(withdraw.error)}</StatusMessage>}
    </li>
  );
}

/** One job, everything needed on site: address (maps), tenant tap-to-call, entry window, actions. */
export function ContractorJobDetail() {
  const { id = '' } = useParams();
  const query = useQuery({ queryKey: jobKey(id), queryFn: () => api.contractor.job(id) });
  const [docErr, setDocErr] = useState<string | null>(null);

  if (query.isLoading) return <Loading label="Loading job…" />;
  if (query.isError || !query.data)
    return (
      <>
        <p>
          <Link to="/contractor">← My jobs</Link>
        </p>
        <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />
      </>
    );

  const d = query.data;
  const job = d.job;
  const address = locationLabel(job.location);
  const mapsQuery = [job.location.line1, job.location.line2, job.location.city, job.location.state, job.location.postalCode]
    .filter(Boolean)
    .join(', ');
  const terminal = job.status === 'closed' || job.status === 'cancelled';

  return (
    <div className="stack">
      <p style={{ margin: 0 }}>
        <Link to="/contractor">← My jobs</Link>
      </p>
      <h1 style={{ marginBottom: 0 }}>{job.title}</h1>
      <p className="row" style={{ margin: 0 }}>
        <span className="badge">{job.status}</span>
        <span className="muted">
          {job.category} · {job.priority}
        </span>
      </p>

      <section className="card stack" aria-labelledby="where-h">
        <h2 id="where-h">Where</h2>
        <p style={{ margin: 0 }}>{address || '—'}</p>
        {mapsQuery && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in Maps
          </a>
        )}
        <p className="muted" style={{ margin: 0 }}>
          Permission to enter: {job.permissionToEnter ? 'yes' : 'no — coordinate with the tenant'}
          {job.availability ? ` · Tenant availability: ${job.availability}` : ''}
        </p>
      </section>

      {d.tenantContacts.length > 0 && (
        <section className="card stack" aria-labelledby="tenant-h">
          <h2 id="tenant-h">Tenant contact</h2>
          <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {d.tenantContacts.map((t, i) => (
              <li key={i}>
                <strong>{t.name}</strong>
                <div className="row">
                  {t.phone && (
                    <a className="button-link" href={`tel:${t.phone.replace(/[^\d+]/g, '')}`}>
                      Call {t.phone}
                    </a>
                  )}
                  {t.phone && (
                    <a className="button-link" href={`sms:${t.phone.replace(/[^\d+]/g, '')}`}>
                      Text
                    </a>
                  )}
                  {t.email && <a href={`mailto:${t.email}`}>{t.email}</a>}
                </div>
              </li>
            ))}
          </ul>
          <p className="field-hint">Use this only to arrange access for this job.</p>
        </section>
      )}

      <section className="card stack" aria-labelledby="job-h">
        <h2 id="job-h">The job</h2>
        <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{job.description}</p>
        {d.entryWindows.length > 0 ? (
          <p style={{ margin: 0 }}>
            <strong>Entry window:</strong> {formatDateTime(d.entryWindows[0]!.entryStartAt)}
            {d.entryWindows[0]!.entryEndAt ? ` – ${formatDateTime(d.entryWindows[0]!.entryEndAt)}` : ''}
          </p>
        ) : (
          <p className="muted" style={{ margin: 0 }}>No entry scheduled yet.</p>
        )}
      </section>

      <section className="stack" aria-labelledby="sched-h">
        <h2 id="sched-h">Scheduling</h2>
        {d.proposals.length > 0 && (
          <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {d.proposals.slice(0, 5).map((p) => (
              <ProposalRow key={p.id} jobId={id} p={p} />
            ))}
          </ul>
        )}
        {d.canProposeSchedule ? (
          <ProposeForm id={id} hasOpen={d.proposals.some((p) => p.status === 'proposed')} />
        ) : (
          d.proposals.length === 0 && <p className="muted">Scheduling isn’t available for this job right now.</p>
        )}
      </section>
      {!terminal && d.allowedTransitions.includes('resolved') && <ResolveForm id={id} />}

      <section className="stack" aria-labelledby="photos-h">
        <h2 id="photos-h">Photos</h2>
        {docErr && <StatusMessage tone="err">{docErr}</StatusMessage>}
        {d.photos.length === 0 ? (
          <p className="muted">No photos yet.</p>
        ) : (
          <ul className="row" style={{ listStyle: 'none', padding: 0 }}>
            {d.photos.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    setDocErr(null);
                    openDocument(id, p.documentId).catch((e) => setDocErr(errorMessage(e)));
                  }}
                >
                  {p.kind === 'submission' ? 'Tenant photo' : 'My photo'} · {formatDateTime(p.createdAt)}
                </button>
              </li>
            ))}
          </ul>
        )}
        {!terminal && <PhotoUpload id={id} />}
      </section>

      <section className="stack" aria-labelledby="costs-h">
        <h2 id="costs-h">My costs</h2>
        {d.costs.length === 0 ? (
          <p className="muted">No costs submitted for this job.</p>
        ) : (
          <ul className="stack" style={{ listStyle: 'none', padding: 0 }}>
            {d.costs.map((c) => (
              <CostRow key={c.id} jobId={id} cost={c} locked={terminal} />
            ))}
          </ul>
        )}
        {!terminal && <CostForm id={id} />}
      </section>

      {!terminal && <NoteForm id={id} />}

      {d.history.length > 0 && (
        <section className="stack" aria-labelledby="hist-h">
          <h2 id="hist-h">History</h2>
          <ul className="stack" style={{ listStyle: 'none', padding: 0 }}>
            {d.history.map((h) => (
              <li key={h.id} className="card">
                <span className="badge">{h.toStatus}</span> <span className="muted">{formatDateTime(h.createdAt)}</span>
                {h.mine && <span className="muted"> · you</span>}
                {h.note && <p style={{ margin: '0.25rem 0 0' }}>{h.note}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
